import { useState, useEffect } from "react";
import { Card, Table, Select, Modal, Input, Button, Form, message } from "antd";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";

function pickAr(overlay) {
  return typeof overlay?.ar === "string" ? overlay.ar : "";
}

export default function IngredientsBuilder({
  setIngredients,
  initialIngredients = [],
}) {
  const [ingredientsData, setIngredientsData] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingIngredient, setEditingIngredient] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    if (initialIngredients?.length) {
      const withKeys = initialIngredients.map((item, index) => ({
        key: `init-${index}`,
        ...item,
        nameAr: pickAr(item.nameI18n) || item.nameAr || "",
        formAr: pickAr(item.formI18n) || item.formAr || "",
      }));
      setIngredientsData(withKeys);
      syncParent(withKeys);
    }
  }, [initialIngredients]);

  const syncParent = (data) => {
    const cleaned = data
      .map(({ key, nameAr, formAr, nameI18n, formI18n, ...rest }) => {
        const name = rest.name?.trim();
        const quantity = rest.quantity?.toString().trim();
        const formVal = rest.form?.trim();
        const nameArTrim = (nameAr || pickAr(nameI18n) || "").trim();
        const formArTrim = (formAr || pickAr(formI18n) || "").trim();

        const next = {
          ...rest,
          name,
          quantity,
          form: formVal || "-",
        };

        if (nameArTrim) next.nameI18n = { ar: nameArTrim };
        if (formArTrim) next.formI18n = { ar: formArTrim };

        return next;
      })
      .filter((i) => i.name);

    setIngredients(cleaned);
  };

  const handleAddClick = () => {
    setEditingIngredient(null);
    form.resetFields();
    setIsModalOpen(true);
  };

  const handleEdit = (record) => {
    setEditingIngredient(record);

    form.setFieldsValue({
      name: record.name,
      nameAr: record.nameAr || pickAr(record.nameI18n) || "",
      qty: record.quantity,
      unit: record.unit,
      form: record.form === "-" ? "" : record.form,
      formAr: record.formAr || pickAr(record.formI18n) || "",
    });

    setIsModalOpen(true);
  };

  const handleDelete = (key) => {
    const updated = ingredientsData.filter((item) => item.key !== key);
    setIngredientsData(updated);
    syncParent(updated);
  };

  const handleSaveIngredient = (values) => {
    const cleanName = values.name?.trim();
    const cleanQty =
      values.qty !== undefined && values.qty !== null
        ? values.qty.toString().trim()
        : "";

    const cleanForm = values.form?.trim();
    const cleanNameAr = values.nameAr?.trim() || "";
    const cleanFormAr = values.formAr?.trim() || "";

    if (!cleanName) return;

    const duplicate = ingredientsData.find(
      (item) =>
        item.name.toLowerCase() === cleanName.toLowerCase() &&
        item.key !== editingIngredient?.key
    );

    if (duplicate) {
      return message.error("Ingredient already added");
    }

    let updated;

    if (editingIngredient) {
      updated = ingredientsData.map((item) =>
        item.key === editingIngredient.key
          ? {
              ...item,
              name: cleanName,
              nameAr: cleanNameAr,
              quantity: cleanQty,
              unit: values.unit,
              form: cleanForm || "-",
              formAr: cleanFormAr,
            }
          : item
      );
    } else {
      const newIngredient = {
        key: `new-${Date.now()}`,
        name: cleanName,
        nameAr: cleanNameAr,
        quantity: cleanQty,
        unit: values.unit,
        form: cleanForm || "-",
        formAr: cleanFormAr,
      };

      updated = [...ingredientsData, newIngredient];
    }

    setIngredientsData(updated);
    syncParent(updated);

    setIsModalOpen(false);
    setEditingIngredient(null);
    form.resetFields();
  };

  const columns = [
    {
      title: "#",
      width: 60,
      render: (_, __, index) => index + 1,
    },
    {
      title: "Ingredient",
      dataIndex: "name",
    },
    {
      title: "Ingredient (AR)",
      dataIndex: "nameAr",
      render: (val, record) => val || pickAr(record.nameI18n) || "—",
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      width: 100,
    },
    {
      title: "Unit",
      dataIndex: "unit",
      width: 100,
    },
    {
      title: "Form",
      dataIndex: "form",
    },
    {
      title: "Form (AR)",
      dataIndex: "formAr",
      render: (val, record) => val || pickAr(record.formI18n) || "—",
    },
    {
      title: "Action",
      width: 100,
      render: (_, record) => (
        <div style={{ display: "flex", gap: 10 }}>
          <EditOutlined
            style={{ cursor: "pointer" }}
            onClick={() => handleEdit(record)}
          />
          <DeleteOutlined
            style={{ color: "red", cursor: "pointer" }}
            onClick={() => handleDelete(record.key)}
          />
        </div>
      ),
    },
  ];

  return (
    <Card className="recipe-section">
      <div className="section-title">
        <span className="section-bar" />
        <h3>3. Ingredients Builder</h3>
      </div>

      <Table
        columns={columns}
        dataSource={ingredientsData}
        pagination={false}
        bordered
        rowKey="key"
        scroll={{ x: true }}
      />

      <div
        className="add-ingredient-link"
        onClick={handleAddClick}
        style={{ cursor: "pointer", marginTop: 12 }}
      >
        + Add Ingredient Row
      </div>

      <Modal
        open={isModalOpen}
        centered
        footer={null}
        onCancel={() => {
          setIsModalOpen(false);
          setEditingIngredient(null);
          form.resetFields();
        }}
      >
        <div style={{ marginBottom: 12 }}>
          <h3 style={{ margin: 0 }}>
            {editingIngredient ? "Edit Ingredient" : "Add Ingredient"}
          </h3>
          <div style={{ color: "#8c8c8c", fontSize: 13 }}>
            Specify the ingredient details
          </div>
        </div>

        <Form layout="vertical" form={form} onFinish={handleSaveIngredient}>
          <div style={{ display: "flex", gap: 12 }}>
            <Form.Item
              label="Ingredient"
              name="name"
              style={{ flex: 1 }}
              rules={[{ required: true, message: "Enter ingredient" }]}
            >
              <Input placeholder="Onion" />
            </Form.Item>

            <Form.Item label="Quantity" name="qty" style={{ width: 140 }}>
              <Input type="number" min="0" step="0.1" placeholder="1" />
            </Form.Item>
          </div>

          <Form.Item label="Ingredient (Arabic)" name="nameAr">
            <Input placeholder="بصل" dir="rtl" />
          </Form.Item>

          <Form.Item label="Unit" name="unit">
            <Select placeholder="Select unit">
              <Select.OptGroup label="Volume">
                <Select.Option value="tsp">tsp</Select.Option>
                <Select.Option value="tbsp">tbsp</Select.Option>
                <Select.Option value="cup">cup</Select.Option>
                <Select.Option value="ml">ml</Select.Option>
              </Select.OptGroup>

              <Select.OptGroup label="Weight">
                <Select.Option value="g">g</Select.Option>
                <Select.Option value="oz">oz</Select.Option>
                <Select.Option value="lb">lb</Select.Option>
              </Select.OptGroup>

              <Select.OptGroup label="Count / Size">
                <Select.Option value="whole">whole</Select.Option>
                <Select.Option value="piece">piece</Select.Option>
                <Select.Option value="slice">slice</Select.Option>
                <Select.Option value="clove">clove</Select.Option>
                <Select.Option value="small">small</Select.Option>
                <Select.Option value="medium">medium</Select.Option>
                <Select.Option value="large">large</Select.Option>
              </Select.OptGroup>
            </Select>
          </Form.Item>

          <Form.Item label="Ingredient Form (Optional)" name="form">
            <Input placeholder="e.g. minced" />
          </Form.Item>

          <Form.Item label="Ingredient Form (Arabic)" name="formAr">
            <Input placeholder="مثال: مفروم" dir="rtl" />
          </Form.Item>

          <Button type="primary" htmlType="submit" block>
            {editingIngredient ? "Update Ingredient" : "Add Ingredient"}
          </Button>
        </Form>
      </Modal>
    </Card>
  );
}
